/**
 * Finds the service a deploy should bind a hostname to (journey.md rung 4,
 * G8): compose's own `ports:` host-publishing is dropped by design — Comebye
 * routes through domains instead (ADR-0026) — so the wizard needs to know,
 * BEFORE deploying, which service the operator meant to expose and on what
 * container port.
 *
 * This is a deliberately small line/indent scanner over the SAME
 * "services:" -> "<name>:" -> "ports:" shape the mock compose importer
 * already parses (see mockClient's parseComposeForMock) — not a YAML parser,
 * and no YAML dependency is added for it. It only needs one fact: does some
 * service declare a port, and which container port is it. `ports:` entries
 * are read in EITHER shape compose allows: the short string
 * `[host:]container[/proto]`, or the long mapping form
 * (`target`/`published`/`protocol`) — always taking `target` (the CONTAINER
 * side) for the long form, never `published` (the host side); an entry that
 * doesn't resolve to either shape (or a mapping missing `target`) is simply
 * skipped, same as "no ports declared" on that entry — never a parse error,
 * since this is advisory (the wizard offers to skip) rather than the
 * authoritative import (that's still the server's compose importer). Any
 * compose shape entirely outside this subset (anchors, multi-doc) likewise
 * yields no routed service rather than an error.
 */
export interface RoutedService {
  serviceName: string;
  containerPort: number;
}

interface ParsedLine {
  indent: number;
  trimmed: string;
}

function toLines(compose: string): ParsedLine[] {
  return compose.split(/\r?\n/).map((line) => ({
    indent: line.length - line.trimStart().length,
    trimmed: line.trim(),
  }));
}

function stripQuotes(value: string): string {
  const trimmed = value.trim();
  if (trimmed.length >= 2 && (trimmed[0] === '"' || trimmed[0] === "'") && trimmed.endsWith(trimmed[0])) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}

function parseInlineArray(value: string): string[] {
  const trimmed = value.trim();
  if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) return [];
  const inner = trimmed.slice(1, -1).trim();
  if (!inner) return [];
  return inner.split(",").map((item) => stripQuotes(item.trim())).filter(Boolean);
}

/** Parses one compose `ports:` short-syntax entry — `[host:]container[/proto]`
 * (e.g. "80", "8080:80", "127.0.0.1:8080:80", "80/tcp") — and returns the
 * CONTAINER port (the last `:`-segment, protocol suffix stripped). Returns
 * `null` for anything outside that shape (e.g. a mapping-syntax port). */
function containerPortOf(entry: string): number | null {
  const withoutProto = stripQuotes(entry).split("/")[0];
  const parts = withoutProto.split(":");
  const last = parts[parts.length - 1];
  const port = Number(last);
  return Number.isInteger(port) && port > 0 && port <= 65535 ? port : null;
}

function firstContainerPort(entries: string[]): number | null {
  for (const entry of entries) {
    const port = containerPortOf(entry);
    if (port !== null) return port;
  }
  return null;
}

/**
 * Parses ONE `ports:` list item starting at `lines[start]` (a line whose
 * trimmed text begins with `"- "`) and returns the entry to feed into
 * `containerPortOf`, plus the index of the line after this whole item
 * (including any of its own indented continuation lines).
 *
 * Two shapes:
 *   - SHORT: `- "8080:80"` — the text after "- " is the whole entry.
 *   - LONG (mapping): `- target: 80` optionally followed by more indented
 *     `key: value` lines (`published:`, `protocol:`, `host_ip:`, in any
 *     order). Detected by the text after "- " itself looking like a
 *     `key: value` pair. Only `target` is read (the CONTAINER port,
 *     audit condition 3) — `published` is the HOST port and must never be
 *     mistaken for it. A mapping with no `target` yields no entry at all
 *     rather than misreading `published`/`protocol` as if they were a
 *     short-syntax port string.
 */
function parsePortListItem(lines: ParsedLine[], start: number): { entry: string | null; next: number } {
  const itemIndent = lines[start].indent;
  const afterDash = lines[start].trimmed.slice(2);
  let i = start + 1;

  const mappingMatch = /^([A-Za-z0-9_]+)\s*:\s*(.*)$/.exec(afterDash);
  if (!mappingMatch) {
    // Short syntax: the whole "- ..." line is one scalar entry.
    return { entry: stripQuotes(afterDash), next: i };
  }

  const mapping: Record<string, string> = {};
  mapping[mappingMatch[1]] = stripQuotes(mappingMatch[2]);
  while (i < lines.length && (lines[i].trimmed === "" || lines[i].indent > itemIndent)) {
    const kv = /^([A-Za-z0-9_]+)\s*:\s*(.*)$/.exec(lines[i].trimmed);
    if (kv) mapping[kv[1]] = stripQuotes(kv[2]);
    i++;
  }
  return { entry: mapping.target ?? null, next: i };
}

/**
 * Returns the first service (in file order) that declares a container port
 * via `ports:`, or `null` when no service does. `compose` is the raw text
 * the wizard already has (recipe or pasted) — never re-fetched or re-parsed
 * server-side; this is advisory only.
 */
export function findRoutedService(compose: string): RoutedService | null {
  const lines = toLines(compose);
  const servicesIdx = lines.findIndex((l) => /^services\s*:\s*$/.test(l.trimmed));
  if (servicesIdx < 0) return null;

  let i = servicesIdx + 1;
  while (i < lines.length && (lines[i].trimmed === "" || lines[i].trimmed.startsWith("#"))) i++;
  if (i >= lines.length) return null;

  const serviceIndent = lines[i].indent;

  while (i < lines.length) {
    const line = lines[i];
    if (line.trimmed === "" || line.trimmed.startsWith("#")) {
      i++;
      continue;
    }
    if (line.indent < serviceIndent) break; // left the services block
    if (line.indent > serviceIndent) {
      i++;
      continue;
    }

    const nameMatch = /^([A-Za-z0-9][A-Za-z0-9_.-]*)\s*:\s*$/.exec(line.trimmed);
    if (!nameMatch) {
      i++;
      continue;
    }
    const name = nameMatch[1];
    i++;

    while (i < lines.length && (lines[i].trimmed === "" || lines[i].indent > serviceIndent)) {
      const inner = lines[i];
      if (inner.trimmed === "" || inner.trimmed.startsWith("#")) {
        i++;
        continue;
      }
      const keyMatch = /^([A-Za-z0-9_]+)\s*:\s*(.*)$/.exec(inner.trimmed);
      if (!keyMatch) {
        i++;
        continue;
      }
      const key = keyMatch[1];
      const rest = keyMatch[2].trim();
      const keyIndent = inner.indent;
      i++;

      if (key === "ports") {
        const entries: string[] = [];
        if (rest) {
          entries.push(...parseInlineArray(rest));
        } else {
          while (i < lines.length && lines[i].trimmed.startsWith("- ")) {
            const { entry, next } = parsePortListItem(lines, i);
            if (entry !== null) entries.push(entry);
            i = next;
          }
        }
        const port = firstContainerPort(entries);
        if (port !== null) {
          return { serviceName: name, containerPort: port };
        }
        continue;
      }
      // Unrecognized/uninteresting key: skip its own indent-bounded block.
      while (i < lines.length && (lines[i].trimmed === "" || lines[i].indent > keyIndent)) i++;
    }
  }

  return null;
}
